import { Breadcrumb } from 'antd';
import type { ItemType } from 'antd/lib/breadcrumb/Breadcrumb';
import { Link, useLocation, useParams } from 'react-router-dom';
import type { StoredUser } from '../../hooks/types';
import { useNotify } from '../../hooks/useNotify';
import { usePermission } from '../../hooks/usePermission';
import useQueryTeamById from '../../hooks/useQueryTeamById';
import useQueryUserById from '../../hooks/useQueryUserById';
import { onApolloError } from '../../lib/graphql/helpers';
import { userFullName } from '../../lib/utils';
import routes from '../App/routes';
import { pathItemsToSkip, PathItemComponent } from './util';

type BreadcrumbProps = {
  readonly currentUser?: StoredUser;
};

/**
 * Generates Breadcrumb element by iterating through the URL path.
 *
 * @remarks
 * The URL will be divided and added into an array as individual pathElements.
 * Then it maps each elements into a Breadcrumb Item.
 * Since Breadcrumb uses URL to determine its elements, it is necessary to convert the 'teamId'
 * section of URL to the actual team name.
 * The teamId will be first identified by comparing with data from useQueryTeamId.
 * The team name will then be filtered to remove possible whitespace.
 * Since all team elements is under /Team, for any pathElements like 'new' or 'settings'
 * need to be replaced with pathItem (since if queryItem.id !== pathItem, it returns '').
 *
 * Then the path element will be capitalised.
 *
 * e.g) 12345abced -> ['example team name' , ''] -> ['example team name'] -> 'example team name'
 * -> Example Team Name
 *
 * @returns a Breadcrumb of current location (e.g. Home > Team > Example Team Name > Roster)
 */
const Breadcrumbs = (props: BreadcrumbProps) => {
  const currentPath = useLocation();
  const { notify } = useNotify();

  const { teamId } = useParams<{ readonly teamId: string | undefined }>();

  const { userId } = useParams<{ readonly userId: string | undefined }>();

  const permission = usePermission(teamId, props.currentUser);

  const { data: teamResult } = useQueryTeamById(
    undefined,
    (e) => notify.error(e.message),
    teamId,
    teamId === undefined
  );

  const { data: userResult } = useQueryUserById(
    onApolloError(notify.error),
    userId
  );

  const currentPathItems: readonly string[] = currentPath.pathname
    .split('/')
    .filter((pathElement) => pathElement.length !== 0);

  const currentLocation: readonly ItemType[] = currentPathItems
    .filter((pathItem) => !pathItemsToSkip.includes(pathItem))
    .map((pathItem, idx) => {
      const url = `/${currentPathItems.slice(0, idx + 1).join('/')}`;

      const convertedTeamPath: readonly string[] =
        teamResult?.team.map((queryItem) => {
          return queryItem.id === pathItem ? queryItem.name : '';
        }) ?? [];

      const convertedUserPath =
        userResult?.user_by_pk?.id === pathItem
          ? userFullName(userResult.user_by_pk)
          : undefined;

      const filterWhitespacePath: readonly string[] = convertedTeamPath
        .filter((teamName) => {
          return teamName !== '';
        })
        .flat();

      const replacePathItem = () => {
        if (convertedUserPath !== undefined) {
          return convertedUserPath;
        }

        if (filterWhitespacePath.length === 0) {
          return pathItem;
        }

        return filterWhitespacePath.toString();
      };

      return {
        key: pathItem,
        title: (
          <Link to={url}>
            <PathItemComponent
              pathItem={replacePathItem()}
              permission={permission.teamManageNonLocalImt}
            />
          </Link>
        ),
      };
    });

  const currentBreadcrumbItems: readonly ItemType[] =
    currentPath.pathname === '/'
      ? []
      : [{ key: 'home', title: <Link to={routes.home}>Home</Link> }];

  return (
    <Breadcrumb
      style={{
        paddingTop: '25px',
        paddingBottom: '25px',
        textTransform: 'capitalize',
      }}
      separator=">"
      items={[...currentBreadcrumbItems, ...currentLocation]}
    />
  );
};

export default Breadcrumbs;
